import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  InputSearch,
  TableData,
  Pagination,
  UniversalFilter,
  ActionDropdown,
  ActionDropdownAction,
  ColumnSettings,
} from '@/components';
import { useTableColumns } from '@/hooks/useTableColumns';
import { Column, Model, Oem } from '@/types/common';
import {
  BUSINESS_UNIT_LABEL,
  BusinessUnit,
  COMPANY_ENTITY_LABEL,
  COMPANY_ENTITY_OPTIONS,
  CompanyEntity,
  CreatePurchaseOrderBody,
  PurchaseOrder,
  PurchaseOrderStatus,
  PURCHASE_ORDER_STATUS_LABEL,
  STATUS_BADGE,
} from '@/types/purchase-order';
import {
  createPurchaseOrder,
  getPurchaseOrders,
} from '@/services/api/purchaseOrders';
import { getListOem } from '@/services/api/oems';
import { getListModel } from '@/services/api/models';
import { getListProvider, Provider } from '@/services/api/providers';
import {
  showErrorToast,
  showSuccessToast,
} from '@/components/toast/globalToast';
import { extractApiErrorMessage, getUserData, getUserEmail } from '@/utils';
import CreatePurchaseOrderModal from './components/CreatePurchaseOrderModal';

const getPoId = (po: PurchaseOrder): string => po.id || po.ID || '';

// DD/MM/YYYY, HH:MM (24-hour) — matches the detail page's date formatting.
const formatCreatedDateTime = (value?: string | null) => {
  if (!value) return '-';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return new Intl.DateTimeFormat('id-ID', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  })
    .format(parsed)
    .replace(/\./g, ':');
};

const STATUS_OPTIONS = Object.values(PurchaseOrderStatus).map((value) => ({
  value,
  label: PURCHASE_ORDER_STATUS_LABEL[value],
}));

const BUSINESS_UNIT_OPTIONS = Object.values(BusinessUnit).map((value) => ({
  value,
  label: BUSINESS_UNIT_LABEL[value],
}));

const PurchaseOrderListPage: React.FC = () => {
  const navigate = useNavigate();
  const [purchaseOrders, setPurchaseOrders] = useState<PurchaseOrder[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [oems, setOems] = useState<Oem[]>([]);
  const [models, setModels] = useState<Model[]>([]);
  const [clients, setClients] = useState<Provider[]>([]);
  const [activeFilters, setActiveFilters] = useState<Record<string, any>>({});
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [actionDropdownOpen, setActionDropdownOpen] = useState<
    string | number | null
  >(null);

  const currentRequestRef = useRef<AbortController | null>(null);
  const requestIdRef = useRef<number>(0);

  const oemNameById = useMemo(() => {
    const map = new Map<string, string>();
    oems.forEach((oem) => map.set(oem.ID, oem.name));
    return map;
  }, [oems]);

  const modelNameById = useMemo(() => {
    const map = new Map<string, string>();
    models.forEach((model) => map.set(model.ID, model.name));
    return map;
  }, [models]);

  const filterConfigs = useMemo(
    () => [
      {
        key: 'oemID',
        title: 'OEM',
        icon: 'ri-building-2-line',
        multiple: false,
        dataSource: oems,
        labelField: 'name',
        valueField: 'ID',
      },
      {
        key: 'modelID',
        title: 'Models',
        icon: 'ri-e-bike-line',
        multiple: false,
        dataSource: models,
        labelFormatter: (model: Model) => `${model?.oemName} - ${model?.name}`,
        valueField: 'ID',
      },
      {
        key: 'entities',
        title: 'Entity',
        icon: 'ri-bank-line',
        multiple: true,
        dataSource: COMPANY_ENTITY_OPTIONS,
        labelField: 'label',
        valueField: 'value',
      },
      {
        key: 'businessUnits',
        title: 'Business Unit',
        icon: 'ri-briefcase-line',
        multiple: true,
        dataSource: BUSINESS_UNIT_OPTIONS,
        labelField: 'label',
        valueField: 'value',
      },
      {
        key: 'statuses',
        title: 'Status',
        icon: 'ri-filter-3-fill',
        multiple: true,
        dataSource: STATUS_OPTIONS,
        labelField: 'label',
        valueField: 'value',
      },
    ],
    [models, oems]
  );

  useEffect(() => {
    const loadFilterData = async () => {
      try {
        const [oemsResponse, modelsResponse, providersResponse] =
          await Promise.all([
            getListOem({ page: '1', size: '1000' }),
            getListModel({ page: '1', size: '1000' }),
            getListProvider({ page: '1', size: '9999' }),
          ]);
        setOems(oemsResponse.data);
        setModels(modelsResponse.data);
        setClients(providersResponse.data);
      } catch (error) {}
    };

    loadFilterData();
  }, []);

  const activeFiltersString = useMemo(() => {
    return JSON.stringify(activeFilters);
  }, [activeFilters]);

  useEffect(() => {
    const fetchPurchaseOrders = async () => {
      const currentRequestId = ++requestIdRef.current;

      if (currentRequestRef.current) {
        currentRequestRef.current.abort();
      }

      const abortController = new AbortController();
      currentRequestRef.current = abortController;

      setIsLoading(true);

      try {
        const response = await getPurchaseOrders({
          page: currentPage,
          size: pageSize,
          ...(searchTerm.trim() ? { search: searchTerm.trim() } : {}),
          ...(activeFilters.statuses?.length > 0
            ? { statuses: activeFilters.statuses }
            : {}),
          ...(activeFilters.businessUnits?.length > 0
            ? { businessUnits: activeFilters.businessUnits }
            : {}),
          ...(activeFilters.entities?.length > 0
            ? { entities: activeFilters.entities }
            : {}),
          ...(activeFilters.oemID ? { oemID: activeFilters.oemID } : {}),
          ...(activeFilters.modelID
            ? { modelID: activeFilters.modelID }
            : {}),
        });

        if (
          !abortController.signal.aborted &&
          currentRequestId === requestIdRef.current
        ) {
          setPurchaseOrders(response.data);
          setTotalRecords(response.pagination.total);
          currentRequestRef.current = null;
        }
      } catch (error: any) {
        if (
          !abortController.signal.aborted &&
          currentRequestId === requestIdRef.current
        ) {
          showErrorToast(
            'Error',
            extractApiErrorMessage(error, 'Failed to fetch purchase orders')
          );
          currentRequestRef.current = null;
        }
      } finally {
        if (
          !abortController.signal.aborted &&
          currentRequestId === requestIdRef.current
        ) {
          setIsLoading(false);
        }
      }
    };

    fetchPurchaseOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, pageSize, refreshTrigger, searchTerm, activeFiltersString]);

  useEffect(() => {
    const handleClickOutside = () => {
      setActionDropdownOpen(null);
    };

    if (actionDropdownOpen !== null) {
      document.addEventListener('click', handleClickOutside);
      return () => {
        document.removeEventListener('click', handleClickOutside);
      };
    }
  }, [actionDropdownOpen]);

  const columns: Column<PurchaseOrder>[] = [
    {
      key: 'code',
      title: 'Purchase Order ID',
      width: '140px',
      render: (code: string) => (
        <span className="font-medium text-gray-900">{code}</span>
      ),
    },
    {
      key: 'oemID',
      title: 'OEM',
      width: '140px',
      render: (oemID: string) => (
        <span className="text-sm">{oemNameById.get(oemID) || '-'}</span>
      ),
    },
    {
      key: 'modelID',
      title: 'Model',
      width: '140px',
      render: (modelID: string) => (
        <span className="text-sm">{modelNameById.get(modelID) || '-'}</span>
      ),
    },
    {
      key: 'totalUnits',
      title: 'Received / Total Units',
      width: '150px',
      align: 'center',
      render: (totalUnits: number, record: PurchaseOrder) => {
        const received = record.receivedUnits ?? 0;
        const isComplete = totalUnits > 0 && received >= totalUnits;
        return (
          <span className="text-sm">
            <span
              className={`font-semibold ${
                isComplete
                  ? 'text-green-600'
                  : received > 0
                    ? 'text-blue-600'
                    : 'text-gray-400'
              }`}
            >
              {received}
            </span>
            <span className="text-gray-400"> / </span>
            <span className="text-gray-900">{totalUnits}</span>
          </span>
        );
      },
    },
    {
      key: 'entity',
      title: 'Entity',
      width: '100px',
      render: (entity: CompanyEntity | null) => (
        <span className="text-sm">
          {(entity && COMPANY_ENTITY_LABEL[entity]) || entity || '-'}
        </span>
      ),
    },
    {
      key: 'requestingBusinessUnit',
      title: 'Business Unit',
      width: '150px',
      render: (businessUnit: BusinessUnit) => (
        <span className="text-sm">
          {BUSINESS_UNIT_LABEL[businessUnit] || businessUnit || '-'}
        </span>
      ),
    },
    {
      key: 'status',
      title: 'Status',
      width: '120px',
      render: (status: PurchaseOrderStatus) => (
        <span
          className={`px-2 py-1 rounded-full text-xs font-medium ${
            STATUS_BADGE[status] || 'bg-gray-100 text-gray-800'
          }`}
        >
          {PURCHASE_ORDER_STATUS_LABEL[status] || status}
        </span>
      ),
    },
    {
      key: 'createdAt',
      title: 'Created Date',
      width: '150px',
      sortable: true,
      render: (date) => formatCreatedDateTime(date),
    },
    {
      key: 'createdBy',
      title: 'Created By',
      width: '150px',
    },
    {
      key: 'actions',
      title: '',
      width: 'w-20',
      align: 'center',
      render: (_: any, record: PurchaseOrder, index: number) => {
        const uniqueId = getPoId(record) || `temp-${index}`;
        return (
          <ActionDropdown
            record={record}
            isOpen={actionDropdownOpen === uniqueId}
            onToggle={() => handleToggleDropdown(uniqueId)}
            actions={createActions(record)}
            ariaLabel={`Actions for purchase order ${record.code}`}
          />
        );
      },
    },
  ];

  // Column customization (show/hide + reorder), persisted per browser.
  const { visibleColumns, columnSettingsProps } = useTableColumns<PurchaseOrder>({
    storageKey: 'purchase-orders',
    columns,
  });

  const handleSearch = (value: string) => {
    const searchChanged = searchTerm !== value;
    setSearchTerm(value);
    if (searchChanged) {
      setCurrentPage(1);
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleFiltersApply = (filters: Record<string, any>) => {
    const filtersChanged =
      JSON.stringify(activeFilters) !== JSON.stringify(filters);
    setActiveFilters(filters);
    if (filtersChanged) {
      setCurrentPage(1);
    }
  };

  const handleFiltersClear = () => {
    const hasFilters = Object.keys(activeFilters).length > 0;
    setActiveFilters({});
    if (hasFilters) {
      setCurrentPage(1);
    }
  };

  const handleToggleDropdown = useCallback((uniqueId: string | number) => {
    setActionDropdownOpen((prev) => (prev === uniqueId ? null : uniqueId));
  }, []);

  const createActions = useCallback(
    (purchaseOrder: PurchaseOrder): ActionDropdownAction[] => [
      {
        key: 'view',
        label: 'Detail',
        icon: 'ri-eye-line',
        onClick: () => navigate(`/purchase-orders/${getPoId(purchaseOrder)}`),
      },
    ],
    [navigate]
  );

  const handleModalClose = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const handleCreateSubmit = useCallback(
    async (data: CreatePurchaseOrderBody) => {
      setIsCreating(true);
      try {
        await createPurchaseOrder({
          ...data,
          createdBy: getUserData()?.name || getUserEmail(),
        });
        showSuccessToast('Success', 'Purchase order created successfully');
        setIsModalOpen(false);
        setRefreshTrigger((prev) => prev + 1);
      } catch (error) {
        showErrorToast(
          'Error',
          extractApiErrorMessage(error, 'Failed to create purchase order')
        );
      } finally {
        setIsCreating(false);
      }
    },
    []
  );

  return (
    <div className="bg-white p-6 max-w-full mx-auto">
      <div className="mb-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-[24px] font-semibold text-gray-900 mb-1">
              Purchase Order
            </h1>
            <p className="text-[14px] text-gray-600">
              Manage vehicle purchase orders and unit receiving.
            </p>
          </div>
          <Button
            variant="primary"
            size="small"
            onClick={() => setIsModalOpen(true)}
          >
            <i className="ri-add-line text-[12px] mr-1"></i>
            Create Purchase Order
          </Button>
        </div>
      </div>

      <div className="rounded-lg p-2 mb-4">
        <div className="flex items-center justify-end gap-4">
          <div className="flex items-center gap-2">
            <InputSearch
              placeholder="Search by PO number..."
              value={searchInput}
              onChange={setSearchInput}
              onSearch={handleSearch}
              onClear={() => handleSearch('')}
              debounceMs={300}
              size="small"
            />

            <UniversalFilter
              filters={filterConfigs}
              currentFilters={activeFilters}
              onApply={handleFiltersApply}
              onClear={handleFiltersClear}
            />

            <ColumnSettings {...columnSettingsProps} />
          </div>
        </div>
      </div>

      <TableData
        columns={visibleColumns}
        data={purchaseOrders}
        loading={isLoading}
        onRowClick={(purchaseOrder) =>
          navigate(`/purchase-orders/${getPoId(purchaseOrder)}`)
        }
      />

      <Pagination
        currentPage={currentPage}
        totalItems={totalRecords}
        itemsPerPage={pageSize}
        onPageChange={handlePageChange}
        onPageSizeChange={(newPageSize) => {
          setPageSize(newPageSize);
          setCurrentPage(1);
        }}
      />

      <CreatePurchaseOrderModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        onSubmit={handleCreateSubmit}
        isLoading={isCreating}
        oems={oems}
        models={models}
        clients={clients}
      />
    </div>
  );
};

export default PurchaseOrderListPage;
